import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Link, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './components/LanguageSelector';
import ThemeToggle from './components/ThemeToggle';
import OfflineIndicator from './components/OfflineIndicator';
import { performanceApi } from './services/api';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Auth pages
import Login from './pages/auth/Login';
import Setup from './pages/auth/Setup';
import Unauthorized from './pages/auth/Unauthorized';

// User management
import UserList from './pages/users/UserList';
import UserForm from './pages/users/UserForm';
import AuditLogList from './pages/audit-logs/AuditLogList';

// Main app pages
import Dashboard from './pages/Dashboard';
import MemberList from './pages/members/MemberList';
import MemberForm from './pages/members/MemberForm';
import MemberDetail from './pages/members/MemberDetail';
import PaymentList from './pages/payments/PaymentList';
import PaymentForm from './pages/payments/PaymentForm';
import ExpenseList from './pages/expenses/ExpenseList';
import ExpenseForm from './pages/expenses/ExpenseForm';
import ExpenseDetail from './pages/expenses/ExpenseDetail';
import ExpensePaymentList from './pages/expense-payments/ExpensePaymentList';
import ExpensePaymentForm from './pages/expense-payments/ExpensePaymentForm';
import FinancialReport from './pages/reports/FinancialReport';
import DataManagement from './pages/data/DataManagement';
import PerformanceMonitoring from './pages/performance/PerformanceMonitoring';
import Settings from './pages/admin/Settings';
import Services from './pages/admin/Services';

// Reminder pages
import ReminderList from './pages/reminders/ReminderList';
import ReminderForm from './pages/reminders/ReminderForm';
import ReminderTest from './pages/reminders/ReminderTest';
import TemplateList from './pages/reminders/TemplateList';
import TemplateForm from './pages/reminders/TemplateForm';
import ReminderLogList from './pages/reminders/ReminderLogList';

// Navigation component with role-based visibility
const Navigation = () => {
  const { currentUser, logout, hasPermission } = useAuth();
  const { t } = useTranslation(['common', 'navigation', 'services']);
  const { themeMode, isDarkMode, changeThemeMode } = useTheme();
  const [performanceEnabled, setPerformanceEnabled] = useState(false);
  
  // Disable performance monitoring
  useEffect(() => {
    setPerformanceEnabled(false);
  }, []);
  
  if (!currentUser) return null;
  
  return (
    <nav className="navbar navbar-expand-lg bg-body-tertiary rounded shadow-sm mb-4" aria-label="Duebit Navigation">
      <div className="container-fluid">
        <NavLink to="/" className="navbar-brand">
          <img src="/favicon-32x32.png" alt="Duebit Logo" height="30" className="me-2" />
          {t('app.name')}
        </NavLink>
        <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
            {/* Dashboard */}
            <li className="nav-item">
              <NavLink to="/" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"} aria-current={({ isActive }) => isActive ? "page" : undefined}>
                <i className="fas fa-chart-line me-1"></i>
                {t('navigation.dashboard')}
              </NavLink>
            </li>
            
            {/* Management Dropdown */}
            <li className="nav-item dropdown">
              <a className="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                <i className="fas fa-tasks me-1"></i>
                {t('navigation.management')}
              </a>
              <ul className="dropdown-menu">
                {hasPermission('members', 'view') && (
                  <li>
                    <NavLink to="/members" className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}>
                      <i className="fas fa-users me-1"></i>
                      {t('navigation.members')}
                    </NavLink>
                  </li>
                )}
                
                {hasPermission('payments', 'view') && (
                  <li>
                    <NavLink to="/payments" className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}>
                      <i className="fas fa-money-bill-alt me-1"></i>
                      {t('navigation.payments')}
                    </NavLink>
                  </li>
                )}
                
                {hasPermission('reminders', 'view') && (
                  <li>
                    <NavLink to="/reminders" className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}>
                      <i className="fas fa-bell me-1"></i>
                      {t('navigation.reminders')}
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
            
            {/* Expenses */}
            {hasPermission('expenses', 'view') && (
              <li className="nav-item">
                <NavLink to="/expenses" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
                  <i className="fas fa-file-invoice-dollar me-1"></i>
                  {t('navigation.expenses')}
                </NavLink>
              </li>
            )}
            
            {/* Reports */}
            {hasPermission('reports', 'view') && (
              <li className="nav-item">
                <NavLink to="/reports" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
                  <i className="fas fa-chart-pie me-1"></i>
                  {t('navigation.reports')}
                </NavLink>
              </li>
            )}
            
            {/* Admin Dropdown */}
            {(currentUser.role === 'owner' || currentUser.role === 'admin') && (
              <li className="nav-item dropdown">
                <a className="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <i className="fas fa-shield-alt me-1"></i>
                  {t('navigation.admin')}
                </a>
                <ul className="dropdown-menu">
                  <li>
                    <NavLink to="/settings" className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}>
                      <i className="fas fa-cogs me-1"></i>
                      {t('navigation.settings')}
                    </NavLink>
                  </li>
                  
                  {hasPermission('services', 'view') && (
                    <li>
                      <NavLink to="/services" className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}>
                        <i className="fas fa-cloud me-1"></i>
                        {t('services:title')}
                      </NavLink>
                    </li>
                  )}
                  
                  {hasPermission('users', 'view') && (
                    <li>
                      <NavLink to="/users" className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}>
                        <i className="fas fa-user-cog me-1"></i>
                        {t('navigation.users')}
                      </NavLink>
                    </li>
                  )}
                  
                  {hasPermission('audit_logs', 'view') && (
                    <li>
                      <NavLink to="/audit-logs" className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}>
                        <i className="fas fa-history me-1"></i>
                        {t('navigation.auditLogs')}
                      </NavLink>
                    </li>
                  )}
                  
                  <li>
                    <NavLink to="/data" className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}>
                      <i className="fas fa-database me-1"></i>
                      {t('navigation.data')}
                    </NavLink>
                  </li>
                  
                  {performanceEnabled && (
                    <li>
                      <NavLink to="/performance" className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}>
                        <i className="fas fa-tachometer-alt me-1"></i>
                        {t('navigation.performance')}
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            )}
          </ul>
          
          <div className="d-flex align-items-center">
            <LanguageSelector />
            <div className="ms-2">
              <ThemeToggle />
            </div>
            <span className="mx-3">
              <i className="fas fa-user me-1"></i>
              {currentUser.username}
            </span>
            <button 
              className="btn btn-outline-secondary btn-sm"
              onClick={logout}
            >
              <i className="fas fa-sign-out-alt me-1"></i>
              {t('navigation.logout')}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [performanceEnabled, setPerformanceEnabled] = useState(false);
  
  // Disable performance monitoring
  useEffect(() => {
    setPerformanceEnabled(false);
  }, []);
  
  return (
    <div className="app">
      <ToastContainer position="top-right" autoClose={3000} />
      <OfflineIndicator />
      {currentUser && <Navigation />}
      <main className={currentUser ? "container py-4" : ""}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Dashboard />} />
            
            {/* Member routes */}
            <Route element={<ProtectedRoute resource="members" action="view" />}>
              <Route path="/members" element={<MemberList />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="members" action="view" />}>
              <Route path="/members/:id" element={<MemberDetail />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="members" action="create" />}>
              <Route path="/members/new" element={<MemberForm />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="members" action="update" />}>
              <Route path="/members/:id/edit" element={<MemberForm />} />
            </Route>
            
            {/* Payment routes */}
            <Route element={<ProtectedRoute resource="payments" action="view" />}>
              <Route path="/payments" element={<PaymentList />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="payments" action="create" />}>
              <Route path="/payments/new" element={<PaymentForm />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="payments" action="update" />}>
              <Route path="/payments/:id/edit" element={<PaymentForm />} />
            </Route>
            
            {/* Expense routes */}
            <Route element={<ProtectedRoute resource="expenses" action="view" />}>
              <Route path="/expenses" element={<ExpenseList />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="expenses" action="view" />}>
              <Route path="/expenses/:id" element={<ExpenseDetail />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="expenses" action="create" />}>
              <Route path="/expenses/new" element={<ExpenseForm />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="expenses" action="update" />}>
              <Route path="/expenses/:id/edit" element={<ExpenseForm />} />
            </Route>
            
            {/* Expense payment routes */}
            <Route element={<ProtectedRoute resource="expense_payments" action="view" />}>
              <Route path="/expense-payments" element={<ExpensePaymentList />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="expense_payments" action="create" />}>
              <Route path="/expense-payments/new" element={<ExpensePaymentForm />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="expense_payments" action="update" />}>
              <Route path="/expense-payments/:id/edit" element={<ExpensePaymentForm />} />
            </Route>
            
            {/* Report routes */}
            <Route element={<ProtectedRoute resource="reports" action="view" />}>
              <Route path="/reports" element={<FinancialReport />} />
            </Route>
            
            {/* User management routes */}
            <Route element={<ProtectedRoute resource="users" action="view" />}>
              <Route path="/users" element={<UserList />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="users" action="create" />}>
              <Route path="/users/new" element={<UserForm />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="users" action="update" />}>
              <Route path="/users/:id/edit" element={<UserForm />} />
            </Route>
            
            {/* Audit log routes */}
            <Route element={<ProtectedRoute resource="audit_logs" action="view" />}>
              <Route path="/audit-logs" element={<AuditLogList />} />
            </Route>
            
            {/* Data management route - only for owners and admins */}
            <Route element={<ProtectedRoute />}>
              <Route path="/data" element={
                currentUser?.role === 'owner' || currentUser?.role === 'admin' ? (
                  <DataManagement />
                ) : (
                  <Unauthorized />
                )
              } />
            </Route>
            
            {/* Performance monitoring route - only for owners and admins and if enabled */}
            {performanceEnabled && (
              <Route element={<ProtectedRoute />}>
                <Route path="/performance" element={
                  currentUser?.role === 'owner' || currentUser?.role === 'admin' ? (
                    <PerformanceMonitoring />
                  ) : (
                    <Unauthorized />
                  )
                } />
              </Route>
            )}
            
            {/* Settings route - only for owners and admins */}
            <Route element={<ProtectedRoute />}>
              <Route path="/settings" element={
                currentUser?.role === 'owner' || currentUser?.role === 'admin' ? (
                  <Settings />
                ) : (
                  <Unauthorized />
                )
              } />
            </Route>
            
            {/* Services route - only for owners and admins */}
            <Route element={<ProtectedRoute resource="services" action="view" />}>
              <Route path="/services" element={<Services />} />
            </Route>
            
            {/* Reminder routes */}
            <Route element={<ProtectedRoute resource="reminders" action="view" />}>
              <Route path="/reminders" element={<ReminderList />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="reminders" action="create" />}>
              <Route path="/reminders/new" element={<ReminderForm />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="reminders" action="update" />}>
              <Route path="/reminders/:id/edit" element={<ReminderForm />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="reminders" action="view" />}>
              <Route path="/reminders/:id/test" element={<ReminderTest />} />
            </Route>
            
            {/* Reminder template routes */}
            <Route element={<ProtectedRoute resource="reminders" action="view" />}>
              <Route path="/reminder-templates" element={<TemplateList />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="reminders" action="create" />}>
              <Route path="/reminder-templates/new" element={<TemplateForm />} />
            </Route>
            
            <Route element={<ProtectedRoute resource="reminders" action="update" />}>
              <Route path="/reminder-templates/:id/edit" element={<TemplateForm />} />
            </Route>
            
            {/* Reminder log routes */}
            <Route element={<ProtectedRoute resource="reminders" action="view" />}>
              <Route path="/reminder-logs" element={<ReminderLogList />} />
            </Route>
          </Route>
          
          {/* Catch-all route - redirect to login or setup */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </main>
      {currentUser && (
        <footer className="container mt-5 py-3 border-top">
          <p className="text-center text-muted">{t('app.name')} &copy; {new Date().getFullYear()}</p>
        </footer>
      )}
    </div>
  );
}

export default App;
